import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FlaskConical, Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { acars } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isDesktop, useAppVersion, useElectronSim } from "@/lib/electron";
import {
  SETUPS, CHECKLIST_VERSION, checklistFor, summarise, detectSetup,
} from "@/lib/beta-checklist";

/**
 * Beta test (requested 2026-10-05). A tester flies one flight against the
 * checklist for their setup and sends the result to FlyVAC.
 *
 * The draft lives in this machine's localStorage until it is sent: a tester
 * marks items over a two-hour flight, restarts VACARS5 on purpose (it is one
 * of the checks) and must not lose their marks. Sending it posts to
 * /api/beta/reports, which stores it and announces it on Discord; admins read
 * it on Beta reports.
 *
 * Only pilots listed in BETA_PILOTS (and admins) get the checklist; the server
 * says which in user.is_beta and refuses reports from anyone else.
 */

const DRAFT_KEY = "vacars5.beta.draft";
const LOG_LEAD_MS = 30 * 60 * 1000;   // log from half an hour before the first mark

const emptyDraft = () => ({ setup: null, startedAt: null, flight: "", aircraft: "", results: {}, comments: "" });

function loadDraft() {
  try { return { ...emptyDraft(), ...(JSON.parse(localStorage.getItem(DRAFT_KEY)) || {}) }; }
  catch { return emptyDraft(); }
}
function saveDraft(d) {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch { /* the page still works this visit */ }
}

/** Checklist text with its one kind of markup, <code>, as elements — no innerHTML. */
function Rich({ text }) {
  const parts = String(text || "").split(/<code>(.*?)<\/code>/g);
  return parts.map((part, i) => (i % 2
    ? <code key={i} className="font-mono text-xs bg-secondary border border-border rounded px-1 break-all">{part}</code>
    : <span key={i}>{part}</span>));
}

const STATUS = [
  ["pass", "Pass", "bg-emerald-600/15 text-emerald-600 dark:text-emerald-400 border-emerald-600/40"],
  ["fail", "Fail", "bg-primary/15 text-primary border-primary/50"],
  ["skip", "Skip", "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/40"],
];

export default function BetaTest() {
  const { user } = useAuth();
  if (!user?.is_beta) {
    return (
      <div className="p-8 max-w-3xl" data-testid="beta-closed">
        <div className="card-flat p-10 text-center text-sm text-muted-foreground">
          <FlaskConical className="w-6 h-6 mx-auto mb-2" />
          The beta test is open to invited pilots only. Ask an admin if you'd like to help test.
        </div>
      </div>
    );
  }
  return <BetaChecklist />;
}

function BetaChecklist() {
  const [draft, setDraft] = useState(loadDraft);
  const [sent, setSent] = useState(null);
  const [includeLog, setIncludeLog] = useState(true);
  const [openNotes, setOpenNotes] = useState({});

  const appVersion = useAppVersion();
  const { data: active } = useQuery({ queryKey: ["acars-active"], queryFn: acars.active });
  const sim = useElectronSim(active);
  const detected = detectSetup(window.electronAPI?.platform, sim.status?.sim);
  const setup = draft.setup || detected;

  useEffect(() => { saveDraft(draft); }, [draft]);

  // Prefill the flight from the active flight once, while the box is empty.
  useEffect(() => {
    if (!active?.id || draft.flight) return;
    const f = `${active.operator || ""}${active.flt || ""} ${active.orig || ""}-${active.dest || ""}`.trim();
    setDraft((d) => (d.flight ? d : { ...d, flight: f }));
  }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const phases = useMemo(() => (setup ? checklistFor(setup) : []), [setup]);
  const tally = setup ? summarise(setup, draft.results) : null;

  const mark = (id, title, status) => setDraft((d) => {
    const cur = d.results[id] || {};
    const next = { ...cur, status: cur.status === status ? undefined : status, t: title, at: new Date().toISOString() };
    if (status === "fail" && next.status) setOpenNotes((o) => ({ ...o, [id]: true }));
    return { ...d, startedAt: d.startedAt || Date.now(), results: { ...d.results, [id]: next } };
  });
  const note = (id, title, text) => setDraft((d) => ({
    ...d, results: { ...d.results, [id]: { ...(d.results[id] || {}), note: text, t: title } },
  }));

  const send = useMutation({
    mutationFn: async () => {
      const applies = new Set(phases.flatMap((p) => p.items.map((i) => i.id)));
      const results = Object.fromEntries(Object.entries(draft.results)
        .filter(([id, r]) => applies.has(id) && r.status)
        .map(([id, r]) => [id, { status: r.status, note: r.note || null, t: r.t || null, at: r.at || null }]));
      let log_excerpt = null;
      if (includeLog && window.electronAPI?.diagnostics) {
        const since = (draft.startedAt || Date.now()) - LOG_LEAD_MS;
        const r = await window.electronAPI.diagnostics.logTail(since);
        if (r?.ok) log_excerpt = r.text;
      }
      return api.post("/beta/reports", {
        setup, checklist_version: CHECKLIST_VERSION, app_version: appVersion || null,
        flight: draft.flight || null, aircraft: draft.aircraft || null,
        live_id: active?.id || null, total: tally.total, results,
        comments: draft.comments || null, log_excerpt,
      }).then((r) => r.data);
    },
    // Sent: clear the marks now, not only on "Start a new test". They lived on in
    // this page and in localStorage, so leaving and coming back (or restarting)
    // reloaded the report just sent as if it were still to do (2026-10-17). A
    // failed send keeps them, as before.
    onSuccess: (r) => {
      setDraft(emptyDraft());
      setOpenNotes({});
      setSent(r);
      toast.success(`Report #${r.id} sent. Thank you!`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not send the report. Your marks are kept; try again."),
  });

  const startOver = () => { setDraft(emptyDraft()); setSent(null); setOpenNotes({}); };
  const marked = tally ? tally.total - tally.open : 0;

  if (sent) {
    return (
      <div className="p-8 max-w-3xl" data-testid="beta-sent">
        <div className="card-flat p-10 text-center">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-emerald-500" />
          <div className="text-xl font-bold mb-1">Report #{sent.id} sent</div>
          <p className="text-sm text-muted-foreground mb-6">
            Thanks for testing. The team has your results{includeLog && isDesktop() ? " and the log from this flight" : ""}.
          </p>
          <button onClick={startOver} className="text-xs bg-primary text-primary-foreground px-4 py-2 rounded-md font-mono uppercase tracking-wider hover:brightness-110">
            Start a new test
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl" data-testid="beta-page">
      <div className="mb-6">
        <div className="overline mb-1">Beta</div>
        <h1 className="text-3xl font-bold tracking-tight">Test flight</h1>
        <div className="acbar" />
        <p className="text-sm text-muted-foreground mt-4 max-w-2xl">
          Fly one flight and work down the list in order. Mark each item Pass, Fail or Skip, and add a
          note when something is off. Your marks are kept on this computer until you send them, even if
          you restart VACARS5. Items marked <em>optional</em> need extra effort.
        </p>
        <p className="text-sm mt-3 max-w-2xl border-l-4 border-primary pl-3">
          Bid and check your logbook on the <strong>new myVAC at myvac.flyvac.org</strong>, not the
          current one on vacanada.org. The two are separate; your beta flights are still credited to you.
        </p>
      </div>

      <section className="card-flat p-5 mb-6 grid gap-4 sm:grid-cols-3" data-testid="beta-setup">
        <label className="block">
          <span className="overline block mb-1.5">Your setup</span>
          <select
            id="beta-setup"
            value={setup || ""}
            onChange={(e) => setDraft((d) => ({ ...d, setup: e.target.value || null }))}
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
          >
            <option value="">Choose…</option>
            {Object.entries(SETUPS).map(([k, s]) => (
              <option key={k} value={k}>{s.label}{k === detected ? " (detected)" : ""}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="overline block mb-1.5">Flight</span>
          <input id="beta-flight" value={draft.flight} placeholder="e.g. ACA418 CYYZ-CYUL"
            onChange={(e) => setDraft((d) => ({ ...d, flight: e.target.value }))}
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm font-mono" />
        </label>
        <label className="block">
          <span className="overline block mb-1.5">Aircraft</span>
          <input id="beta-aircraft" value={draft.aircraft} placeholder="e.g. Fenix A320"
            onChange={(e) => setDraft((d) => ({ ...d, aircraft: e.target.value }))}
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm" />
        </label>
      </section>

      {!setup ? (
        <div className="card-flat p-8 text-sm text-muted-foreground text-center">
          Choose your setup above to see its checklist.
        </div>
      ) : (
        <>
          <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border py-2 mb-4 flex flex-wrap gap-x-4 gap-y-1 text-xs font-mono tabular-nums" data-testid="beta-tally">
            <span>{marked} of {tally.total} marked</span>
            <span className="text-emerald-600 dark:text-emerald-400">{tally.pass} pass</span>
            <span className="text-primary">{tally.fail} fail</span>
            <span className="text-amber-600 dark:text-amber-400">{tally.skip} skip</span>
            <span className="text-muted-foreground ml-auto">{SETUPS[setup].label}</span>
          </div>

          {phases.map((p, pi) => (
            <section key={p.id} className="mb-8" aria-labelledby={`beta-${p.id}`}>
              <h2 id={`beta-${p.id}`} className="text-sm font-bold uppercase tracking-widest border-b-2 border-foreground pb-1 mb-1">
                {p.name}
              </h2>
              <p className="text-xs text-muted-foreground mb-3">{p.when}</p>
              <div className="space-y-2">
                {p.items.map((it, ii) => {
                  const r = draft.results[it.id] || {};
                  const showNote = openNotes[it.id] || !!r.note;
                  return (
                    <article key={it.id} data-testid={`beta-item-${it.id}`}
                      className={`card-flat p-4 border-l-4 ${r.status === "pass" ? "border-l-emerald-500" : r.status === "fail" ? "border-l-primary" : r.status === "skip" ? "border-l-amber-500" : "border-l-border"}`}>
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
                        <span className="font-mono text-xs text-muted-foreground">{pi + 1}.{ii + 1}</span>
                        <span className="font-semibold flex-1 min-w-0">{it.t}</span>
                        {it.opt && <span className="text-[10px] uppercase tracking-widest border border-border rounded px-1.5 py-0.5 text-muted-foreground">optional</span>}
                      </div>
                      <dl className="grid grid-cols-[4.5rem_1fr] gap-x-3 gap-y-1 text-sm mb-3">
                        <dt className="overline pt-0.5">Do</dt><dd className="min-w-0"><Rich text={it.do} /></dd>
                        <dt className="overline pt-0.5">Expect</dt><dd className="min-w-0"><Rich text={[it.exp, ...it.hints].join(" ")} /></dd>
                        <dt className="overline pt-0.5">If not</dt><dd className="min-w-0 text-muted-foreground"><Rich text={it.bad} /></dd>
                      </dl>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="inline-flex rounded-md border border-border overflow-hidden" role="group" aria-label={`Result for ${it.t}`}>
                          {STATUS.map(([v, label, on]) => (
                            <button key={v} type="button" aria-pressed={r.status === v}
                              data-testid={`beta-${it.id}-${v}`}
                              onClick={() => mark(it.id, it.t, v)}
                              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border-r last:border-r-0 border-border transition-colors ${r.status === v ? on : "text-muted-foreground hover:text-foreground"}`}>
                              {label}
                            </button>
                          ))}
                        </div>
                        {!showNote && (
                          <button type="button" className="text-xs text-primary hover:underline"
                            onClick={() => setOpenNotes((o) => ({ ...o, [it.id]: true }))}>
                            Add note
                          </button>
                        )}
                      </div>
                      {showNote && (
                        <textarea id={`beta-note-${it.id}`} value={r.note || ""} rows={2}
                          aria-label={`Note for ${it.t}`}
                          placeholder="What you saw: numbers, messages, log lines…"
                          onChange={(e) => note(it.id, it.t, e.target.value)}
                          className="mt-3 w-full bg-background border border-border rounded-md px-3 py-2 text-sm" />
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          ))}

          <section className="card-flat p-5 space-y-4" data-testid="beta-send">
            <label className="block">
              <span className="overline block mb-1.5">Anything else</span>
              <textarea id="beta-comments" value={draft.comments} rows={3}
                placeholder="Overall impressions, things the list didn't cover…"
                onChange={(e) => setDraft((d) => ({ ...d, comments: e.target.value }))}
                className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm" />
            </label>
            {isDesktop() && (
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" id="beta-include-log" checked={includeLog}
                  onChange={(e) => setIncludeLog(e.target.checked)} className="mt-1" />
                <span>
                  Include the VACARS5 log from this flight. It shows what the app saw and did; passwords
                  and sign-in tokens are removed before it is sent.
                </span>
              </label>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" onClick={() => send.mutate()} disabled={send.isPending || marked === 0}
                data-testid="beta-submit"
                className="bg-primary text-primary-foreground text-sm font-bold uppercase tracking-widest px-6 py-3 rounded-md hover:brightness-110 disabled:opacity-60 flex items-center gap-2">
                {send.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send report
              </button>
              <span className="text-xs text-muted-foreground">
                {marked === 0 ? "Mark at least one item first." : tally.open ? `${tally.open} items not marked yet; you can still send.` : "Every item marked."}
              </span>
              <button type="button" onClick={startOver} className="ml-auto text-xs text-muted-foreground hover:text-foreground">
                <FlaskConical className="inline w-3 h-3 mr-1" />Clear and start over
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
